<script lang="ts">
  import { checkmarkIcon } from '@nrk/core-icons';
  import type { DrawEntry, Participant } from '@pin/schemas/admin';
  import { formatNumber, formatShare } from '../../utils/format';
  import Icon from '../../lib/Icon.svelte';

  interface CorrectGroupInfo {
    name: string;
    count: number;
    percentage: number;
    variantCount: number;
  }

  interface Props {
    participant: Participant;
    correctGroups: CorrectGroupInfo[];
    guessTotal: number | undefined;
    winner: DrawEntry | null;
  }

  const { participant, correctGroups, guessTotal, winner }: Props = $props();

  const totalCorrectCount = $derived(
    correctGroups.reduce((sum, g) => sum + g.count, 0)
  );

  let copied = $state(false);

  const copyEmail = (email: string) => {
    navigator.clipboard.writeText(email);
    copied = true;
    setTimeout(() => (copied = false), 2000);
  };
</script>

<div class="unmasked-box">
  <div class="reveal-header">
    <span class="reveal-label">Avmaskert som</span>
    <h3 class="reveal-name">{participant.unmaskedName}</h3>
  </div>

  <div class="correct-section">
    <h4 class="section-label">Riktige svar</h4>
    {#if correctGroups.length > 0}
      <ul class="correct-list">
        {#each correctGroups as group (group.name)}
          <li class="correct-item">
            <span class="group-name">{group.name}</span>
            <span class="group-stats">
              {formatNumber(group.count)} gjett ({formatShare(
                group.count,
                guessTotal
              )}),
              {group.variantCount}
              {group.variantCount === 1 ? 'variant' : 'varianter'}
            </span>
          </li>
        {/each}
      </ul>
      <p class="total-correct">
        Totalt {formatNumber(totalCorrectCount)} riktige gjett ({formatShare(
          totalCorrectCount,
          guessTotal
        )})
      </p>
    {:else}
      <p class="no-correct">Ingen riktige svar registrert.</p>
    {/if}
  </div>

  {#if winner}
    <details class="winner-details">
      <summary class="winner-summary">Vinner</summary>
      <dl class="winner-info">
        <div class="info-row">
          <dt>Navn</dt>
          <dd>{winner.firstName} {winner.lastName ?? ''}</dd>
        </div>
        <div class="info-row">
          <dt>E-post</dt>
          <dd class="email-cell">
            <span class="email">{winner.email}</span>
            <button
              type="button"
              class="copy-btn"
              class:copied
              onclick={() => copyEmail(winner.email)}
              aria-label={copied ? 'Kopiert' : 'Kopier e-post'}
            >
              {#if copied}
                <Icon svg={checkmarkIcon} />
              {:else}
                <svg
                  width="16"
                  height="19"
                  viewBox="0 0 16 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M14.9 13H11C10.45 13 10 13.45 10 14V17.9H7C5.952 17.9 5.1 17.048 5.1 16V7C5.1 5.952 5.952 5.1 7 5.1H13C14.048 5.1 14.9 5.952 14.9 7V13ZM4 7V13.9H3C1.952 13.9 1.1 13.048 1.1 12V3C1.1 1.952 1.952 1.1 3 1.1H9C10.048 1.1 10.9 1.952 10.9 3V4H7C5.35 4 4 5.35 4 7ZM13 4H12V3C12 1.35 10.65 0 9 0H3C1.35 0 0 1.35 0 3V12C0 13.65 1.35 15 3 15H4V16C4 17.65 5.35 19 7 19H9.929L15.999 12.929V7C15.999 5.35 14.649 4 12.999 4H13Z"
                    fill="currentColor"
                  />
                </svg>
              {/if}
            </button>
          </dd>
        </div>
        <div class="info-row">
          <dt>Type</dt>
          <dd>{winner.identityType === 'Adult' ? 'Voksen' : 'Barn'}</dd>
        </div>
        {#if winner.birthYear}
          <div class="info-row">
            <dt>Fødselsår</dt>
            <dd>{winner.birthYear}</dd>
          </div>
        {/if}
      </dl>
    </details>
  {/if}
</div>

<style>
  .unmasked-box {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
    background: light-dark(
      oklch(from var(--success) 95% calc(c * 0.3) h),
      oklch(from var(--success) 20% calc(c * 0.4) h)
    );
    border: var(--border-width) solid
      light-dark(
        oklch(from var(--success) 85% calc(c * 0.4) h),
        oklch(from var(--success) 30% calc(c * 0.5) h)
      );
  }

  .reveal-header {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
  }

  .reveal-label {
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .reveal-name {
    margin: 0;
    font-size: var(--text-xl);
    font-weight: var(--nrk-sans-weight-bold);
  }

  .correct-section {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .section-label {
    margin: 0;
    font-size: var(--text-sm);
    font-weight: var(--nrk-sans-weight-medium);
    color: var(--text-muted);
  }

  .correct-list {
    margin: 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .correct-item {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-2) var(--space-3);
    background: var(--surface-raised);
    border-radius: var(--radius-md);
  }

  .group-name {
    font-weight: var(--nrk-sans-weight-medium);
  }

  .group-stats {
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .total-correct {
    margin: 0;
    font-size: var(--text-sm);
    font-weight: var(--nrk-sans-weight-medium);
  }

  .no-correct {
    margin: 0;
    font-size: var(--text-sm);
    color: var(--text-muted);
  }

  .winner-details {
    border-radius: var(--radius-md);
    background: var(--surface-raised);

    &[open] .winner-summary {
      border-block-end: var(--border-width) solid var(--border);
    }
  }

  .winner-summary {
    padding: var(--space-3);
    font-weight: var(--nrk-sans-weight-medium);
    cursor: pointer;

    &:hover {
      background: var(--surface-base);
    }

    &::marker {
      color: var(--text-muted);
    }
  }

  .winner-info {
    margin: 0;
    padding: var(--space-3);
    display: grid;
    grid-template-columns: auto 1fr;
    gap: var(--space-2) var(--space-4);
  }

  .info-row {
    display: contents;
  }

  .info-row dt {
    color: var(--text-muted);
    font-size: var(--text-sm);
  }

  .info-row dd {
    margin: 0;
  }

  .email-cell {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    min-width: 0;
  }

  .email {
    word-break: break-all;
  }

  .copy-btn {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: var(--space-1);
    background: var(--surface);
    border: var(--border-width) solid var(--border);
    border-radius: var(--radius-sm);
    color: var(--text-muted);
    cursor: pointer;

    &:hover {
      background: var(--surface-raised);
      color: var(--text);
    }

    &.copied {
      color: var(--success);
      border-color: var(--success);
    }
  }
</style>
